import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { useGetProjectUuidQuery } from '../../../../services/projectService';
import classes from './ProjectOpenContent.module.scss';

// Компоненты
import ProjectInfo from './children/ProjectInfo';
import ProjectResults from './children/ProjectResults';
import ProjectChromatogram from './children/ProjectChromatogram';
import ProjectWells from './children/ProjectWells';
import MonitoringStages from './children//MonitoringStages';

// Модальные окна
import MonitoringStage2 from '../../../Modals/MonitoringStage2/MonitoringStage2';
import MonitoringStage3 from '../../../Modals/MonitoringStage3/MonitoringStage3';
import MonitoringStage4 from '../../../Modals/MonitoringStage4/MonitoringStage4';
import MonitoringStage5 from '../../../Modals/MonitoringStage5/MonitoringStage5';
import AddWells from '../../../Modals/AddWells/AddWells';
import LoadData from '../../../Modals/LoadData/LoadData';
import AddGraphic from '../../../Modals/AddGraphic/AddGraphic';
import AddMap from '../../../Modals/AddMap/AddMap';
import AddChroma from '../../../Modals/AddChroma/AddChroma';

interface ProjectOpenContentProps {
  projectUuid?: string;
}

const ProjectOpenContent = ({ projectUuid }: ProjectOpenContentProps) => {
  const navigate = useNavigate();
  const { projectUuid: urlProjectUuid } = useParams<{ projectUuid: string }>();
  const actualProjectUuid = projectUuid || urlProjectUuid;
  
  const [modals, setModals] = useState({
    stage2: false,
    stage3: false,
    stage4: false,
    stage5: false,
    addWells: false,
    loadData: false,
    addGraphic: false,
    addMap: false,
    addChroma: false,
  });
  
  const [userAccessLevel, setUserAccessLevel] = useState<number | null>(null);
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);

  // Проверяем, находимся ли мы на странице мониторинга
  const isMonitoringPage = window.location.pathname.includes('/monitoring');

  // Запрос данных проекта по UUID
  const { data: projectData, isLoading: projectLoading } = useGetProjectUuidQuery(
    { uuid: actualProjectUuid! },
    { skip: !actualProjectUuid }
  );

  useEffect(() => {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (storedUserInfo) {
      try {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        setUserAccessLevel(parsedUserInfo.role?.access_level || 0);
      } catch (error) {
        setUserAccessLevel(0);
      }
    }
  }, []);

  const openModal = (modalName: keyof typeof modals) => {
    setModals(prev => ({ ...prev, [modalName]: true }));
  };

  const closeModal = (modalName: keyof typeof modals) => {
    setModals(prev => ({ ...prev, [modalName]: false }));
  };

  const renderContent = () => {
    const path = window.location.pathname;

    switch (path) {
      case `/platform/project/${actualProjectUuid}`:
        //@ts-ignore
        return <ProjectInfo projectData={projectData} projectUuid={actualProjectUuid} />;
      
      case `/platform/project/${actualProjectUuid}/results`:
        return (
          <ProjectResults 
            userAccessLevel={userAccessLevel}
            projectData={projectData}
            projectUuid={actualProjectUuid}
            //@ts-ignore
            onAddGraphic={() => openModal('addGraphic')}
            onAddMap={() => openModal('addMap')}
          />
        );
      
      case `/platform/project/${actualProjectUuid}/chromatogram`:
        return (
          <ProjectChromatogram 
            projectData={projectData}
            //@ts-ignore
            projectUuid={actualProjectUuid}
            onUpdateChromatogram={() => openModal('addChroma')}
          />
        );
      
      case `/platform/project/${actualProjectUuid}/wells`:
        return <ProjectWells projectData={projectData} projectUuid={actualProjectUuid} />;
      
      default:
        if (path.includes(`/platform/project/${actualProjectUuid}/monitoring`)) {
          return (
            <MonitoringStages 
              userAccessLevel={userAccessLevel}
              projectData={projectData}
              projectUuid={actualProjectUuid}
              navigate={navigate}
              //@ts-ignore
              onOpenModal={openModal}
            />
          );
        }
        return null;
    }
  };

  if (projectLoading) {
    return <div>Загрузка проекта...</div>;
  }

  if (!projectData && !projectLoading) {
    return <div>Проект не найден</div>;
  }

  // Определяем класс в зависимости от наличия MonitoringSidebar
  const mainClass = isMonitoringPage 
    ? `${classes.mainContainer} ${classes.withMonitoringSidebar}`
    : classes.mainContainer

  return (
    <div className={mainClass}>
      {renderContent()}

      {/* Модальные окна с передачей projectUuid */}
      <MonitoringStage2 
        isOpen={modals.stage2} 
        setIsOpen={(value) => setModals(prev => ({ ...prev, stage2: value }))}
        handleCloseModal={() => closeModal('stage2')}
        projectUuid={actualProjectUuid}
      />
      <MonitoringStage3 
        isOpen={modals.stage3} 
        setIsOpen={(value) => setModals(prev => ({ ...prev, stage3: value }))}
        handleCloseModal={() => closeModal('stage3')}
        //@ts-ignore
        projectUuid={actualProjectUuid}
      />
      <MonitoringStage4 
        isOpen={modals.stage4} 
        setIsOpen={(value) => setModals(prev => ({ ...prev, stage4: value }))}
        handleCloseModal={() => closeModal('stage4')}
        //@ts-ignore
        projectUuid={actualProjectUuid}
      />
      <MonitoringStage5 
        isOpen={modals.stage5} 
        setIsOpen={(value) => setModals(prev => ({ ...prev, stage5: value }))}
        handleCloseModal={() => closeModal('stage5')}
        //@ts-ignore
        projectUuid={actualProjectUuid}
      />
      <AddWells 
        isOpen={modals.addWells} 
        setIsOpen={(value) => setModals(prev => ({ ...prev, addWells: value }))}
        handleCloseModal={() => closeModal('addWells')}
        //@ts-ignore
        projectUuid={actualProjectUuid}
      />
      <AddGraphic 
        isOpen={modals.addGraphic} 
        setIsOpen={(value: any) => setModals(prev => ({ ...prev, addGraphic: value }))}
        handleCloseModal={() => closeModal('addGraphic')}
        //@ts-ignore
        projectUuid={actualProjectUuid}
      />
      <AddMap 
        isOpen={modals.addMap} 
        setIsOpen={(value: any) => setModals(prev => ({ ...prev, addMap: value }))}
        handleCloseModal={() => closeModal('addMap')}
        //@ts-ignore
        projectUuid={actualProjectUuid}
      />
      <AddChroma 
        isOpen={modals.addChroma} 
        setIsOpen={(value) => setModals(prev => ({ ...prev, addChroma: value }))}
        handleCloseModal={() => closeModal('addChroma')}
        projectUuid={actualProjectUuid}
      />
    </div>
  );
};

export default ProjectOpenContent;